import type { ReactNode } from "react";
import { ListBox } from "react-aria-components";
import { SimpleMenuPopover } from "../Menu/simpleMenuPopover.js";
import { ComboboxEmpty } from "./comboboxEmpty.js";
import { notePointerInput } from "./comboboxRemoval.js";

export interface ComboboxContentProps<T extends object> {
  /**
   * The options to render, when they aren't the combobox's own `items`. Wins
   * over them if both are given, so prefer putting them on {@link Combobox},
   * where the built-in filter can see them.
   */
  items?: Iterable<T>;
  children: ReactNode | ((item: T) => ReactNode);
  /**
   * Values the option renderer reads besides its item. react-aria caches each
   * rendered option against the item object, so an option whose content depends
   * on anything else — a count, a translation, a per-option disabled flag —
   * keeps rendering the old value until one of these changes.
   */
  dependencies?: readonly unknown[];
  /** Rendered when no options match the filter. Defaults to a "No options found" message. */
  renderEmptyState?: () => ReactNode;
  /** Optional className applied to the Popover. Use to override the menu width. */
  popoverClassName?: string;
}

/**
 * The popup + listbox half of a {@link Combobox}, styled like the standard
 * select/dropdown menus.
 */
export const ComboboxContent = <T extends object>(
  props: ComboboxContentProps<T>,
) => {
  const { items, children, dependencies, renderEmptyState, popoverClassName } =
    props;

  return (
    <SimpleMenuPopover className={popoverClassName}>
      <ListBox
        // Pressing an option is the one unambiguous way to deselect one; see
        // comboboxRemoval.
        onPointerDownCapture={notePointerInput}
        items={items}
        dependencies={dependencies}
        renderEmptyState={
          renderEmptyState ??
          (() => <ComboboxEmpty>No options found</ComboboxEmpty>)
        }
      >
        {children}
      </ListBox>
    </SimpleMenuPopover>
  );
};
